import { useTranslation } from 'react-i18next';
import { CheckCircle2, Hourglass, Layers, Play, XCircle } from 'lucide-react';

import { KpiCard, formatNumber } from '../../../components/design-system/admin';
import type { QueueStats } from './queue-monitor.types';
import { formatDuration } from './queue-monitor.utils';

/** The queue at a glance: how many jobs are in each state, how fast they go. */
export function QueueKpis({ stats }: { stats: QueueStats | null }) {
    const { t } = useTranslation();
    const s = stats?.summary;
    const value = (n?: number) => (s ? formatNumber(n || 0) : '');

    return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5" data-testid="queue-kpis">
            <KpiCard
                label={t('ADMIN_QUEUE.KPI.WAITING')}
                value={value(s?.pending)}
                icon={<Hourglass />}
                loading={!stats}
                testId="kpi-waiting"
            />
            <KpiCard
                label={t('ADMIN_QUEUE.KPI.ACTIVE')}
                value={value(s?.processing)}
                hint={stats ? t('ADMIN_QUEUE.KPI.AVG_TIME', { value: formatDuration(stats.throughput.averageProcessingTimeMs) }) : undefined}
                icon={<Play />}
                loading={!stats}
                testId="kpi-active"
            />
            <KpiCard
                label={t('ADMIN_QUEUE.KPI.GENERATING')}
                value={value(s?.generating_seamless)}
                icon={<Layers />}
                loading={!stats}
                testId="kpi-generating"
            />
            <KpiCard
                label={t('ADMIN_QUEUE.KPI.COMPLETED')}
                value={value(s?.completed)}
                hint={stats ? t('ADMIN_QUEUE.KPI.THROUGHPUT', { value: formatNumber(stats.throughput.completedPerHour, 1) }) : undefined}
                icon={<CheckCircle2 />}
                loading={!stats}
                testId="kpi-completed"
            />
            <KpiCard
                label={t('ADMIN_QUEUE.KPI.FAILED')}
                value={value(s?.failed)}
                icon={<XCircle />}
                invert
                loading={!stats}
                testId="kpi-failed"
            />
        </div>
    );
}
